<template>
  <Modal
    ref="createValidatorModal"
    :title="'Start Validation Session'"
    :height="'100%'"
    @closed="resetData"
  >
    <!-- <template #modal-header /> -->
    <template #modal-main>
      <div class="settings scrollbar">
        <div class="container">
          <div class="static-card">
            <div class="d-flex align-items-center justify-content-between gap-2">
              <h2>Validator Data</h2>
              <HelpTip>
                A validate session enables you to validate a model based on the selected model type.
                The duration of the session can range from a few minutes to several hours,
                depending on the size of the dataset.
              </HelpTip>
            </div>

            <div class="mt-4 mb-2">
              <div class="d-flex align-items-center justify-content-between gap-2">
                <h2 class="mb-2">Select Manage Type</h2>
                <HelpTip>
                  By default, a validate session runs on a cloud server instance equipped with GPU resources.
                  However, you have the option to enable a "User Managed Validator," allowing you to run a custom validation script.
                  Edgefirst Studio APIs provide the endpoints needed to interact with Edgefirst Studio backend server.
                  For more details, please refer to the <a href="https://doc.edgefirst.ai/latest/perception/studio/" target="_blank" rel="noopener noreferrer">Edgefirst Help documentation</a>.
                </HelpTip>
              </div>

              <div>
                <label>
                  <input
                    type="radio"
                    value="cloud"
                    name="manage_group"
                    v-model="manage_type"
                    class="split-radio"
                  />
                  Cloud Managed
                </label>
              </div>

              <div>
                <label>
                  <input
                    type="radio"
                    value="local"
                    name="manage_group"
                    v-model="manage_type"
                    class="split-radio"
                  />
                  User Managed
                </label>
              </div>

              <div>
                <label v-if="false">
                  <input
                    type="radio"
                    value="kubernetes"
                    name="manage_group"
                    v-model="manage_type"
                    class="split-radio"
                  />
                  Kubernetes Managed
                </label>
              </div>

            </div>

            <div class="row mt-3">
              <div class="col-12 settings__control-group">
                <label class="required">Name</label>
                <input
                  v-model="name"
                  class="select"
                  type="text"
                >
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-12 settings__control-group">
                <label>Description</label>
                <textarea
                  v-model="description"
                  class="select"
                  style="height: 64px;"
                  rows="4"
                  placeholder="Enter session description"
                />
              </div>
            </div>
            <h2 class="mt-4">Select Model</h2>
            <div class="row">
              <div class="col settings__control-group pr-2">
                <div class="row full-width">
                  <div class="settings__control-group dataset-selector">
                    <label class="required">Training Session</label>
                    <select v-model="model_training_session" class="select" required>
                      <option disabled :value="null">Select Training Session</option>
                      <option v-for="(s, i) in training_sessions" :key="i" :value="s">
                        {{ `(t-${Number(s.id).toString(16)}) ${s.name}` }}
                      </option>
                    </select>
                  </div>
                </div>
                <div v-if="model_training_session && fileList" class="row mt-3 full-width">
                  <div class="settings__control-group dataset-selector">
                    <label class="required">Model File</label>
                    <select v-model="model_file" class="select" required>
                      <option disabled :value="null">Select Model File</option>
                      <option v-for="(a, i) in fileList" :key="i" :value="a.name">
                        {{ a.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <h2 class="mt-4">Select Dataset</h2>
            <div class="row">
              <div class="col settings__control-group pr-2">
                <div class="row full-width">
                  <SchemaDataset
                    :schemaField="schemaField"
                    v-model:datasetId="model_dataset_id"
                    v-model:tagName="model_tag_name"
                    v-model:annotationSetId="model_annotation_set_id"
                    :enableTagsSelection="true"
                    :enableAnnotationSetSelection="true"
                  />
                </div>
              </div>
            </div>
          </div>
          <div v-if="schema" class="dynamic-card">
            <h2>Settings</h2>
            <SchemaGroup
              :key="componentKey"
              v-model:params="configGroupValues"
              :fields="fields"
            />
          </div>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="footer">
        <button
          class="button modal-action-button"
          :class="{'button-spinner': startingValidator}"
          :disabled="startingValidator"
          @click="startSession"
        >
          Start Session
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import Modal from '@/components/Modal.vue';
import { inputSanitizer, changeSanitizer } from '@/assets/js/utils';
import {
  computed,
} from 'vue';
import { useStore } from 'vuex';
import SchemaGroup from '@/components/Schema/SchemaGroup.vue';
import SchemaDataset from '@/components/Schema/SchemaDataset.vue';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import useTaskModal from '@/composables/useTaskModal.js';
import { event } from 'vue-gtag';
import HelpTip from '../HelpTip.vue';

const $toast = useToast();

export default {
  name: 'StartValidatorModal',
  components: {
    Modal,
    SchemaGroup,
    SchemaDataset,
    HelpTip,
  },
  props: {
    datasetId: {
      type: Number,
      default: 0,
    },
    annotationSetId: {
      type: Number,
      default: 0,
    },
    trainSessionId: {
      type: Number,
      default: 0,
    },
  },
  emits: ['validate-session-started'],
  setup() {
    const store = useStore();
    const currentProject = computed(() => store.state.projects.currentProject);
    const currentExperiment = computed(() => store.state.experiments.currentExperiment);
    const {
      pauseGetTasks,
      resumeGetTasks,
      isGettingTasks,
      getTasks,
    } = useTaskModal();

    return {
      currentProject,
      currentExperiment,
      pauseGetTasks,
      resumeGetTasks,
      isGettingTasks,
      getTasks,
    };
  },
  data() {
    return {
      schemaField: {
        name: "Dataset",
      },
      manage_type:"cloud",
      name: "",
      description: "",
      mode: "model",
      message: '',
      selectedModel: null,
      startingValidator: false,
      schemaList: null,
      schema: null,
      componentKey: 0,
      model_training_session: null,
      model_file: null,
      model_data: 'dataset',
      model_dataset: null,
      model_dataset_id: null,
      model_tag_name: '',
      model_annotation_set: null,
      model_annotation_set_id: null,
      model_snapshot: null,
      legacy_gt_dataset: null,
      legacy_gt_annotation_set: null,
      legacy_target_dataset: null,
      legacy_target_annotation_set: null,
      legacy_confidence: 0.25,
      legacy_iou: 0.5,
      legacy_metric: 'iou',
      legacy_ignore_boxes: 0,
      params: {},
      fileList: null,
      configGroupValues: {},
      training_sessions: [],
      allowedExtensions: ['.keras', '.onnx', '.tflite', '.h5', '.rtm', '.engine', '.dvm', '.hef', '.tensorrt.zip'],
    };
  },
  computed: {
     is_aws() {
      return this.manage_type === "cloud";
    },
    is_local() {
      return this.manage_type === "local";
    },
    is_kubernetes() {
      return this.manage_type === "kubernetes";
    },
    isDebugMode() {
      return this.$store.state.debugMode;
    },
    datasetList() {
      return this.$store.state.datasets.datasetList;
    },
    schemaType() {
      return this.schema?.name;
    },
    fields() {
      return this.schema?.schema;
    },
  },
  watch: {
    configGroupValues: {
      deep: true,
      handler(value) {
        // console.log(value);
      },
    },
    experiments: {
      deep: false,
      async handler(experiments) {
        await Promise.all(experiments.map(async (experiment) => {
          experiment.training_sessions = await this.getTrainingSessionList(experiment.id);
        }));
      },
    },
    async model_training_session() {
      this.model_file = null;
      this.fileList = [];
      this.configGroupValues = {};
      if (this.model_training_session && this.schemaList) {
        const model = this.model_training_session.model;
        this.schema = this.schemaList.find((e) => e.type === model);
        this.fileList = await this.listTrainingSessionArtifacts(this.model_training_session.id);
        this.fileList = this.fileList.filter((e) => this.allowedExtensions.some((ext) => e.name.endsWith(ext)));
      }
      if (!this.schema) {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `No schema of type ${this.model_training_session.model}`,
            type: 'error',
            position: 'top',
          });
        }
    },
    model_dataset() {
      this.model_annotation_set = null;
    },
    legacy_gt_dataset() {
      this.legacy_gt_annotation_set = null;
    },
    legacy_target_dataset() {
      this.legacy_target_annotation_set = null;
    },
  },
  created() {
    this.dataConnect = new DatastoreConnect(this.$store.state.enterpriseServerUrl);
  },
  methods: {
    inputSanitizer,
    changeSanitizer,
    async listTrainingSessionArtifacts(id) {
      const resp = await this.dataConnect.getTrainingSessionArtifacts({ training_session_id: id })
        .catch((error) => {
          console.log('Failed to retrieve training sessions artifacts:', error);
        });
      if (!resp || resp.error || !resp.result) {
        return [];
      }
      return resp.result;
    },
    async showModal() {
      this.resetData();
      await this.getValidatorSchema();
      this.training_sessions = await this.getTrainingSessionList();
      if (this.trainSessionId) {
        if (this.training_sessions && this.training_sessions.length > 0) {
          const session = this.training_sessions.find((e) => e.id === this.trainSessionId);
          if (session) {
            this.model_training_session = session;
          }
        }
      } else {
        if (this.training_sessions && this.training_sessions.length > 0) {
          const session = this.training_sessions[0];
          if (session) {
            this.model_training_session = session;
            this.$nextTick(() => {
              this.model_file = this.fileList && this.fileList.length > 0 ? this.fileList[this.fileList.length - 1] : null;
            });
          }
        }
      }
      if (this.datasetList && this.datasetList.length > 0) {
        if (this.datasetId > 0) {
          const ds = this.datasetList.find((e) => e.id === this.datasetId);
          if (ds) {
            this.model_dataset = ds;
            if (this.annotationSetId > 0) {
              const as = this.model_dataset.annotation_sets.find((e) => e.id === this.annotationSetId);
              if (as) {
                this.$nextTick(() => {
                  this.model_annotation_set = as;
                });
              }
            }
          }
        } else {
          const ds = this.datasetList[0];
          if (ds) {
            this.model_dataset = ds;
            const as = this.model_dataset.annotation_sets ? this.model_dataset.annotation_sets[0] : null;
            if (as) {
              this.$nextTick(() => {
                this.model_annotation_set = as;
              });
            }
          }
        }
      }
      this.$refs.createValidatorModal.showModal();
    },
    async getTrainingSessionList() {
      const resp = await this.dataConnect.getTrainingSessionList({ trainer_id: this.currentExperiment.id, reverse: true })
        .catch((error) => {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Failed to retrieve training sessions: ${error}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Failed to retrieve training sessions: ${resp.error}`,
          type: "error",
          position: "top",
        });
        return [];
      }

      return resp.result;
    },
    resetData() {
      this.name = "";
      this.is_local = false;
      this.name = "";
      this.description = "";
      this.message = "";
      this.is_local = false;
      this.model_training_session = null;
      this.model_file = null;
      this.model_data = 'dataset';
      this.model_dataset = null;
      this.model_annotation_set = null;
      this.model_snapshot = null;
      this.legacy_gt_dataset = null;
      this.legacy_gt_annotation_set = null;
      this.legacy_target_dataset = null;
      this.legacy_target_annotation_set = null;
      this.legacy_confidence = 0.25;
      this.legacy_iou = 0.5;
      this.legacy_metric = 'iou';
      this.legacy_ignore_boxes = 0;
      this.params = {};
      this.fileList = null;
    },
    async getValidatorSchema() {
      await this.dataConnect.getValidatorSchema()
        .then((data) => {
          if (data.result) {
            this.schemaList = data.result;
          } else if (data.error) {
            $toast.open({
              queue: true,
              duration: 5000,
              message: `Failed to retrieve schemas: ${data.error.message}`,
              type: 'error',
              position: 'top',
            });
          }
          this.componentKey += 1;
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Failed to retrieve schemas: ${error}`,
            type: 'error',
            position: 'top',
          });
        });
    },
    async startSession() {
      let val_params = {};
      if (!this.name) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Missing Name',
          type: 'error',
          position: 'top',
        });
        return;
      }
      if (this.mode === 'model') {
        if (!this.model_training_session) {
          $toast.open({
            queue: true,
            duration: 5000,
            message: 'Missing Training Session',
            type: 'error',
            position: 'top',
          });
          return;
        }
        if (!this.model_file) {
          $toast.open({
            queue: true,
            duration: 5000,
            message: 'Missing Model File',
            type: 'error',
            position: 'top',
          });
          return;
        }
        val_params = {
          name: this.name,
          description: this.description,
          params: {
            params: this.configGroupValues,
          },
          val_type: this.schemaType,
          project_id: this.currentProject.id,
          training_session_id: this.model_training_session.id,
          model_file: this.model_file,
          is_local: this.is_local,
          is_kubernetes: this.is_kubernetes,
          is_aws: this.is_aws,
        };
        if (this.model_data === 'dataset') {
          if (!this.model_dataset_id) {
            $toast.open({
              queue: true,
              duration: 5000,
              message: 'Missing Dataset',
              type: 'error',
              position: 'top',
            });
            return;
          }
          val_params.dataset_id = this.model_dataset_id;
          val_params.annotation_set_id = this.model_annotation_set_id;
        } else if (this.model_data === 'snapshot') {
          if (!this.model_snapshot) {
            $toast.open({
              queue: true,
              duration: 5000,
              message: 'Missing Snapshot',
              type: 'error',
              position: 'top',
            });
            return;
          }
          val_params.snapshot_id = this.model_snapshot;
        }
      } else if (this.mode === 'legacy') {
        if (!this.legacy_gt_dataset) {
          $toast.open({
            queue: true,
            duration: 5000,
            message: 'Missing Ground Truth Dataset',
            type: 'error',
            position: 'top',
          });
          return;
        }
        if (!this.legacy_gt_annotation_set) {
          $toast.open({
            queue: true,
            duration: 5000,
            message: 'Missing Ground Truth Annotation Set',
            type: 'error',
            position: 'top',
          });
          return;
        }
        if (!this.legacy_target_dataset) {
          $toast.open({
            queue: true,
            duration: 5000,
            message: 'Missing Target Dataset',
            type: 'error',
            position: 'top',
          });
          return;
        }
        if (!this.legacy_target_annotation_set) {
          $toast.open({
            queue: true,
            duration: 5000,
            message: 'Missing Target Annotation Set',
            type: 'error',
            position: 'top',
          });
          return;
        }
        val_params = {
          name: this.name,
          description: this.description,
          params: {
            iou: this.legacy_iou,
            confidence: this.legacy_confidence,
            metric: this.legacy_metric,
            ignoreBoxes: this.legacy_ignore_boxes,
          },
          val_type: this.schemaType,
          project_id: this.currentProject.id,
          gt_id: this.legacy_gt_annotation_set.id,
          target_id: this.legacy_target_annotation_set.id,
          is_local: this.is_local,
        };
      }
      this.startingValidator = true;
      val_params.type = "validation";
      await this.dataConnect.startCloudInstance(val_params)
        .then((data) => {
          if (data.result) {
            event('create_validation_session_success', {
              category: 'create_validation_session',
              label: 'User successfully created a validation_session',
              user: this.$store.state.user.user.username,
            });
            this.getTasks();
            if (!this.isGettingTasks) {
              this.resumeGetTasks();
            }
            this.resetData();
            this.$refs.createValidatorModal.closeModal();
            this.$emit('validate-session-started');
            $toast.open({
              queue: true,
              duration: 5000,
              message: `Validate session in progress.`,
              type: 'success',
              position: 'top',
            });
          } else if (data.error) {
            event('create_validation_session_failure', {
              category: 'create_validation_session',
              label: 'User failed to create a validation_session',
              user: this.$store.state.user.user.username,
              reason: data.error.message,
            });
            $toast.open({
              queue: true,
              duration: 5000,
              message: data.error.message,
              type: 'error',
              position: 'top',
            });
          }
        })
        .catch((error) => {
          console.log(error);
        });
      this.startingValidator = false;
    },
    getSessionName(session) {
      if (session.name) {
        return session.name;
      } else if (session.docker_task) {
        return session.docker_task.date;
      }
      return 'Unknown Session';
    },
  },
};

</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';
$cardMargin: 20px;
$cardPadding: 16px;

.col {
  padding: 0
}

.settings {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  span {
    width: 100%;
    font-size: 1rem;
    font-weight: 700;
    text-align: left;
    @include themify() {
      color: themed('color-primary');
    }
  }
}

.container {
  display: flex;
  flex-direction: row;
}

h2 {
    color: #250E81;
  }

.static-card {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: $cardPadding;
  text-align: left;
  border-radius: 6px;
  height: fit-content;
  flex: 1 1 auto;
  border: 1px solid #250E81;
  margin: $cardMargin;
  width: 600px;
}

.dynamic-card {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: $cardPadding;
  text-align: left;
  border-radius: 6px;
  height: fit-content;
  flex: 1 1 auto;
  border: 1px solid #250E81;
  margin: $cardMargin;
  width: 600px;
}

@media (max-width: $breakpoint-lg) {
  .settings {
    width: 100%;
  }

  .container {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .static-card {
    width: calc(100% - (2 * #{$cardMargin}));
  }

  .dynamic-card {
    width: calc(100% - (2 * #{$cardMargin}));
  }
}

.footer {
  display: flex;
  flex-direction: row;
  padding: 0.75rem 3rem;
  justify-content: flex-end;
}

.result {
  display: flex;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  font-size: 1rem;
  font-weight:600;
  padding: 10px;
  align-items: center;
  justify-content: center;
  margin-left: 20px;
  margin-right: 20px;

  span {
    width: 100%;
    line-break: anywhere;
  }
}

.error {
  @include themify() {
    background: rgba(182, 94, 94, 0.5) ;
    color: themed('color-error') !important;
  }
}

.dataset-selector{
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: auto;
  padding-right: 0 !important;

  :first-child {
    flex: 1 1 40%;
  }

  :not(:first-child) {
    flex: 1 1 60%;
  }
}

.full-width {
  display: flex;
  flex-direction: row;
  width: 100%;
}

.custom-split-card {
  border-radius: 6px;
  padding: 10px;

  p {
    font-size: 0.9rem;
    font-weight: 700;
  }
}

.custom-split-card + .custom-split-card {
  margin-left: 15px;
}

.split-radio[type="radio"] {
  height: 18px;
  width: 18px;
  @include themify() {
    accent-color: themed('color-primary');
  }
}

:deep() {
  .v-label{
    opacity: 1 !important;
  }
}

.checkbox {
  padding-left: 7px !important
}

.checkbox-label {
  margin-left: 5px;
  font-size: 14px !important;
  font-weight: 600!important;
  letter-spacing: 0 !important;
  @include themify() {
    color: themed('form-label-color') !important;
  }
}

</style>
